// fetch 共通ラッパー。CSRF 自動付与、Cookie 同梱、エラー正規化を担当する。

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(code: string, status: number, message: string, details?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

// 2026-07-06: 并发 refresh lock(同一个 Promise 共享结果)
// - 多个请求同时 401 时,只发一次 refresh
// - 后调用的请求 await 第一个的 promise,共享结果(true/false)
// - 不论刷新成功 / 失败,后续请求都得到一致结论
// - 锁在第一次 refresh 完成后 200ms 内复用,之后释放(让新 401 触发新 refresh)
let refreshing: Promise<boolean> | null = null;

// 認証イベントバス。AuthProvider が購読して session 失効を検知する。
// client.ts から AuthProvider を直接 import すると循環依存になるため EventTarget で疎結合にする。
const authEventTarget = new EventTarget();

export type AuthEvent = "auth:session-lost";

export function subscribeAuthEvents(handler: () => void): () => void {
  const listener = () => handler();
  authEventTarget.addEventListener("auth:session-lost", listener);
  return () => {
    authEventTarget.removeEventListener("auth:session-lost", listener);
  };
}

export async function tryRefresh(): Promise<boolean> {
  // 并发 lock:已在进行中的 refresh 直接共享结果(P0 修复)
  // 原 bug `return true` 永远返回 true,导致共享 promise 失败时
  // 后续并发请求仍认为 refresh 成功 → 一直 retry 失败 → 显示 AUTH_REFRESH_INVALID
  if (refreshing) {
    return refreshing;
  }
  const promise = (async (): Promise<boolean> => {
    try {
      const csrf = getCookie("csrf_token");
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": csrf ?? "" },
      });
      // 不仅看 res.ok,还要看 body.success === true
      // (后端返 200 但 body success=false 也算失败 — cookie 没真设上)
      if (!res.ok) return false;
      try {
        const j = (await res.json()) as { success?: boolean };
        return j.success === true;
      } catch {
        return false;
      }
    } catch {
      return false;
    } finally {
      // 短時間(200ms)内释放锁,让新进来的 401 重新触发 refresh
      setTimeout(() => {
        refreshing = null;
      }, 200);
    }
  })();
  refreshing = promise;
  const ok = await promise;
  // refresh が失敗した場合、AuthProvider に通知して user state を破棄させる。
  // ProtectedRoute が user=null を検知して /login へリダイレクトする。
  if (!ok) {
    authEventTarget.dispatchEvent(new Event("auth:session-lost"));
  }
  return ok;
}

async function doFetch<T>(
  path: string,
  init: RequestInit
): Promise<{ res: Response; json: { success?: boolean; data?: T; error?: { code: string; message: string; details?: unknown } } }> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  if (method !== "GET") {
    const csrf = getCookie("csrf_token");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers,
  });

  let json: { success?: boolean; data?: T; error?: { code: string; message: string; details?: unknown } } = {};
  try {
    json = await res.json();
  } catch {
    // ボディなし
  }
  return { res, json };
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  options: { _retry?: boolean } = {}
): Promise<T> {
  // /api/auth/login と /api/auth/refresh 自身は refresh retry しない
  const noRetry = path === "/auth/login" || path === "/auth/refresh";
  const isAuthEndpoint = path.startsWith("/auth/");

  const { res, json } = await doFetch<T>(path, init);

  // 401 なら refresh を試して 1 回だけ retry
  if (res.status === 401 && !options._retry && !noRetry) {
    const code = json.error?.code;
    // 原則: 普通业务 API 的 401 一律先尝试一次 refresh。
    // 只有 auth 自身接口返回 refresh 相关 401 时，才直接判定 session lost。
    const shouldSkipRefreshForAuthFailure =
      isAuthEndpoint &&
      (code === "AUTH_REFRESH_INVALID" || code === "AUTH_REFRESH_REUSED");

    if (shouldSkipRefreshForAuthFailure) {
      if (json.error) {
        json.error.message = "セッションの有効期限が切れました。再度ログインしてください。";
      }
    } else {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return apiFetch<T>(path, init, { _retry: true });
      }
      if (json.error && (code === "AUTH_REFRESH_INVALID" || code === "AUTH_REFRESH_REUSED")) {
        json.error.message = "セッションの有効期限が切れました。再度ログインしてください。";
      }
    }
  }

  if (!res.ok || json.success === false) {
    // Stage 4B UX fix: 5xx や予期しないレスポンスでも、フロントが安定した
    // 日文 message と error_code を表示できるよう、HTTP ${status} フォールバックを
    // 「サーバーエラー」の日文にする。code 側は UNKNOWN_SERVER_ERROR に固定。
    let code = json.error?.code;
    let message = json.error?.message;
    if (!code || code === "UNKNOWN") {
      // 5xx 系はサーバ側バグの可能性が高いので、code を UNKNOWN_SERVER_ERROR に
      // 固定して UNKNOWN との混同を防ぐ(message は backend があれば優先)。
      if (res.status >= 500) {
        code = "UNKNOWN_SERVER_ERROR";
      } else {
        code = "UNKNOWN";
      }
    }
    if (!message) {
      if (res.status >= 500) {
        message = "サーバーで予期しないエラーが発生しました。しばらく経ってから再度お試しください。";
      } else {
        message = `HTTP ${res.status}`;
      }
    }
    throw new ApiError(code, res.status, message, json.error?.details);
  }

  return json.data as T;
}
